import { useState, useEffect } from 'react';
import { fileApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/Table';
import {
  HardDrive,
  Trash2,
  File,
  FileSpreadsheet,
  FileJson,
  User,
  HardDriveIcon,
} from 'lucide-react';

interface FileRecord {
  id: string;
  userId: string;
  username: string;
  name: string;
  size: number;
  type: string;
  mimeType: string;
  uploadedAt: string;
}

interface StorageStats {
  totalSize: number;
  totalFiles: number;
  userRanking: Array<{
    userId: string;
    username: string;
    totalSize: number;
    fileCount: number;
  }>;
}

export function FilesPage() {
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [stats, setStats] = useState<StorageStats | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [type, setType] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const fetchFiles = async () => {
    setIsLoading(true);
    try {
      const response = await fileApi.listFiles({
        page,
        pageSize,
        type: type || undefined,
      });
      if (response.code === 0) {
        setFiles(response.data.items);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Failed to fetch files:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fileApi.getStorageStats();
      if (response.code === 0) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    fetchFiles();
    fetchStats();
  }, [page, pageSize, type]);

  const handleDelete = async (fileId: string) => {
    if (!confirm('确定要删除此文件吗？')) return;
    try {
      await fileApi.deleteFile(fileId);
      fetchFiles();
      fetchStats();
    } catch (err) {
      console.error('Failed to delete file:', err);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('zh-CN');
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'excel':
        return <FileSpreadsheet className="w-5 h-5 text-green-600" />;
      case 'csv':
        return <FileSpreadsheet className="w-5 h-5 text-blue-600" />;
      case 'json':
        return <FileJson className="w-5 h-5 text-amber-600" />;
      default:
        return <File className="w-5 h-5 text-zinc-600" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">文件存储</h1>
          <p className="text-zinc-500 mt-1">管理用户上传的文件</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">总存储</p>
                <p className="text-2xl font-bold text-zinc-900 mt-1">
                  {stats ? formatFileSize(stats.totalSize) : '-'}
                </p>
              </div>
              <div className="p-3 bg-accent-100 rounded-xl">
                <HardDrive className="w-6 h-6 text-accent-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">总文件数</p>
                <p className="text-2xl font-bold text-zinc-900 mt-1">
                  {stats ? stats.totalFiles : '-'}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <File className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-500">平均文件大小</p>
                <p className="text-2xl font-bold text-zinc-900 mt-1">
                  {stats && stats.totalFiles > 0
                    ? formatFileSize(stats.totalSize / stats.totalFiles)
                    : '-'}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <HardDriveIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-zinc-900"
            >
              <option value="">所有类型</option>
              <option value="excel">Excel</option>
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* User Ranking */}
      {stats && stats.userRanking.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>用户存储排行</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>排名</TableHead>
                  <TableHead>用户</TableHead>
                  <TableHead>文件数</TableHead>
                  <TableHead>存储大小</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.userRanking.slice(0, 5).map((user, index) => (
                  <TableRow key={user.userId}>
                    <TableCell>
                      <span className={`
                        inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold
                        ${index === 0 ? 'bg-amber-100 text-amber-600' :
                          index === 1 ? 'bg-zinc-200 text-zinc-600' :
                          index === 2 ? 'bg-orange-100 text-orange-600' :
                          'bg-zinc-100 text-zinc-500'}
                      `}>
                        {index + 1}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-zinc-400" />
                        <span className="font-medium">{user.username}</span>
                      </div>
                    </TableCell>
                    <TableCell>{user.fileCount}</TableCell>
                    <TableCell>{formatFileSize(user.totalSize)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Files Table */}
      <Card>
        <CardHeader>
          <CardTitle>文件列表</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>文件</TableHead>
                    <TableHead>用户</TableHead>
                    <TableHead>类型</TableHead>
                    <TableHead>大小</TableHead>
                    <TableHead>上传时间</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {files.map((file) => (
                    <TableRow key={file.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center">
                            {getFileIcon(file.type)}
                          </div>
                          <span className="font-medium text-zinc-900">{file.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-zinc-400" />
                          <span>{file.username}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="info">{file.type.toUpperCase()}</Badge>
                      </TableCell>
                      <TableCell>{formatFileSize(file.size)}</TableCell>
                      <TableCell>{formatDate(file.uploadedAt)}</TableCell>
                      <TableCell className="text-right">
                        <button
                          onClick={() => handleDelete(file.id)}
                          className="p-2 rounded-lg text-error-600 hover:bg-error-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination
                currentPage={page}
                totalPages={Math.ceil(total / pageSize)}
                onPageChange={setPage}
                totalItems={total}
                pageSize={pageSize}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
